import React, { useState, useMemo, useRef, useEffect } from 'react';
import '../styles/CompsDistribution.css';
import OptionSwitch from './OptionSwitch';
import { formatterCompact } from '../styles/GlobalStyles';
import { getPriceField } from '../functions';
import { renderHTML } from '../functions';

function CompsDistribution(props) {
    const viewComp = props.viewComp;
    const onCompClick = props.onCompClick;
    const riskSpectrum = props.riskSpectrum;
    const allComps = useMemo(() => props.allComps || [], [props.allComps]);

    const [showAdjustedValues, setShowAdjustedValues] = useState(true);
    const [hoveredBucket, setHoveredBucket] = useState(null);
    const messageBoxRef = useRef(null);

    // Handle clicks on zpid-address links (delegated event handler)
    useEffect(() => {
        const containerElement = messageBoxRef.current;
        if (!containerElement) return;

        const clickHandler = (e) => {
            const el = e.target.closest?.('.zpid-address');
            if (!el) return;
            e.preventDefault();
            const zpid = el.getAttribute('data-zpid');
            if (!zpid || !allComps || !viewComp) return;
            
            const idx = allComps.findIndex(c => {
                const compZpid = c?.zpid || c?.uniqueIds?.zillowId;
                return compZpid && String(compZpid) === String(zpid);
            });
            if (idx !== -1) {
                try { 
                    viewComp(allComps[idx]);
                } catch(err) {
                    console.error('Error viewing comp:', err);
                }
            }
        };

        containerElement.addEventListener('click', clickHandler);
        return () => {
            if (containerElement) {
                containerElement.removeEventListener('click', clickHandler);
            }
        };
    }, [allComps, viewComp]);

    // Map adjustments data to full comp objects
    const compsWithAdjustments = useMemo(() => {
        const adjustmentsData = props.adjustmentsData || [];
        
        if (!Array.isArray(adjustmentsData) || adjustmentsData.length === 0) {
            return [];
        }
        
        return adjustmentsData
            .map(adj => {
                if (!adj || !adj.zpid) return null;
                
                const adjZpid = String(adj.zpid);
                
                // Try multiple matching strategies
                const comp = allComps.find(c => {
                    if (!c) return false;
                    
                    // Try direct zpid match
                    if (c.zpid && String(c.zpid) === adjZpid) return true;
                    
                    // Try uniqueIds.zillowId match
                    if (c.uniqueIds?.zillowId && String(c.uniqueIds.zillowId) === adjZpid) return true;
                    
                    // Try both as numbers if they're numeric
                    const adjZpidNum = Number(adjZpid);
                    if (!isNaN(adjZpidNum)) {
                        if (c.zpid && Number(c.zpid) === adjZpidNum) return true;
                        if (c.uniqueIds?.zillowId && Number(c.uniqueIds.zillowId) === adjZpidNum) return true;
                    }
                    
                    return false;
                });
                
                if (!comp) {
                    console.warn('CompsDistribution: Could not find comp for zpid:', adjZpid);
                    return null;
                }
                
                return {
                    ...comp,
                    adjustedValue: adj.adjusted_value || comp.adjustedValue || comp.price,
                    adjustmentData: adj
                };
            })
            .filter(Boolean);
    }, [props.adjustmentsData, allComps]);

    // Calculate fixed min/max range from all possible values (adjusted values, sale prices, ARV range)
    const { fixedMinValue, fixedMaxValue } = useMemo(() => {
        const arvRange = props.arvRange || {};
        
        if (compsWithAdjustments.length === 0) {
            return { fixedMinValue: 0, fixedMaxValue: 0 };
        }

        // Collect all possible values
        const allValues = [];
        
        // Add all adjusted values
        compsWithAdjustments.forEach(comp => {
            if (comp.adjustedValue && typeof comp.adjustedValue === 'number' && !isNaN(comp.adjustedValue)) {
                allValues.push(comp.adjustedValue);
            }
        });
        
        // Add all sale prices
        compsWithAdjustments.forEach(comp => {
            const salePrice = getPriceField(comp);
            if (salePrice && typeof salePrice === 'number' && !isNaN(salePrice)) {
                allValues.push(salePrice);
            }
        });
        
        // Add ARV range values
        if (arvRange.lower && typeof arvRange.lower === 'number' && !isNaN(arvRange.lower)) {
            allValues.push(arvRange.lower);
        }
        if (arvRange.upper && typeof arvRange.upper === 'number' && !isNaN(arvRange.upper)) {
            allValues.push(arvRange.upper);
        }

        if (allValues.length === 0) {
            return { fixedMinValue: 0, fixedMaxValue: 0 };
        }

        return {
            fixedMinValue: Math.min(...allValues),
            fixedMaxValue: Math.max(...allValues)
        };
    }, [compsWithAdjustments, props.arvRange]);

    // Find selected bucket based on recommendation confidence
    const selectedBucket = useMemo(() => {
        if (!riskSpectrum?.buckets || !riskSpectrum.recommendation?.confidence) return null;
        return riskSpectrum.buckets.find(
            b => b.confidence === riskSpectrum.recommendation.confidence
        );
    }, [riskSpectrum]);

    // Calculate bucket segments for rendering
    const bucketSegments = useMemo(() => {
        if (!riskSpectrum?.buckets || fixedMaxValue === fixedMinValue) return [];
        
        const range = fixedMaxValue - fixedMinValue;
        const selectedConfidence = riskSpectrum.recommendation?.confidence;
        
        return riskSpectrum.buckets.map(bucket => {
            const [lower, upper] = bucket.priceRange || [];
            if (lower === undefined || upper === undefined) return null;
            
            const left = ((lower - fixedMinValue) / range) * 100;
            const width = ((upper - lower) / range) * 100;
            const isSelected = bucket.confidence === selectedConfidence;
            
            return { bucket, left, width, isSelected };
        }).filter(Boolean);
    }, [riskSpectrum, fixedMinValue, fixedMaxValue]);

    // Calculate comp positions based on toggle state, using fixed range
    // Handle overlapping comps by offsetting them with fixed pixel offset (75% visible, 25% overlap)
    const compPositions = useMemo(() => {
        if (compsWithAdjustments.length === 0 || fixedMaxValue === fixedMinValue) {
            return [];
        }

        const range = fixedMaxValue - fixedMinValue;
        // Default dot is 12px, hover is 14px with 2px border
        // 25% overlap = 3px overlap, so offset = 12px - 3px = 9px between centers
        const dotSize = 12; // Base dot size (default state)
        const overlapPercent = 0.25; // 25% overlap
        const pixelOffsetStep = dotSize * (1 - overlapPercent); // 9px offset for 25% overlap
        const positionTolerance = 2; // Consider positions within 2% as overlapping

        // Calculate initial positions
        const initialPositions = compsWithAdjustments.map(comp => {
            const value = showAdjustedValues ? comp.adjustedValue : getPriceField(comp);
            if (typeof value !== 'number' || isNaN(value) || value === null) {
                return { comp, position: 50, pixelOffset: 0 };
            }
            const position = ((value - fixedMinValue) / range) * 100;
            return { comp, position: Math.max(0, Math.min(100, position)), pixelOffset: 0 };
        });

        // Sort by position to process overlaps
        const sorted = [...initialPositions].sort((a, b) => a.position - b.position);
        const finalPositions = [];

        for (let i = 0; i < sorted.length; i++) {
            const current = sorted[i];

            // Find all comps at the same position (within tolerance)
            const samePositionIndices = sorted
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => Math.abs(item.position - current.position) < positionTolerance)
                .map(({ idx }) => idx);

            if (samePositionIndices.length > 1) {
                // Find this comp's index within the overlapping group
                const indexInGroup = samePositionIndices.indexOf(i);
                const groupSize = samePositionIndices.length;
                
                // Center the group: offset from center of group
                const centerOffset = (groupSize - 1) / 2;
                const pixelOffset = (indexInGroup - centerOffset) * pixelOffsetStep;

                finalPositions.push({
                    comp: current.comp,
                    position: current.position,
                    pixelOffset: pixelOffset
                });
            } else {
                finalPositions.push({
                    comp: current.comp,
                    position: current.position,
                    pixelOffset: 0
                });
            }
        }

        return finalPositions;
    }, [compsWithAdjustments, showAdjustedValues, fixedMinValue, fixedMaxValue]);

    // Calculate divider line positions (at min, max, and evenly spaced intermediate positions)
    const dividerPositions = useMemo(() => {
        if (fixedMaxValue === fixedMinValue) {
            return [];
        }
        
        // Always show dividers at min (0%) and max (100%)
        const positions = [0, 100];
        
        // Add evenly spaced intermediate dividers (e.g., at 25%, 50%, 75% if range is large enough)
        // For a cleaner look, we'll add 2 intermediate dividers (at 33.33% and 66.67%)
        positions.push(33.33, 66.67);
        
        return positions.sort((a, b) => a - b);
    }, [fixedMinValue, fixedMaxValue]);

    // Format price range for display
    const formatPriceRange = (priceRange) => {
        if (!Array.isArray(priceRange) || priceRange.length < 2) return '';
        const [lower, upper] = priceRange;
        if (lower === undefined || upper === undefined) return '';
        
        // If range is very small (<2% difference), show single price or midpoint
        if (lower > 0) {
            const rangePercent = ((upper - lower) / lower) * 100;
            if (rangePercent < 2) {
                const midpoint = Math.round((lower + upper) / 2);
                return formatPrice(midpoint);
            }
        }
        
        // Show full range
        return `${formatPrice(lower)} - ${formatPrice(upper)}`;
    };

    const handleCompClick = (comp) => {
        if (viewComp && comp) {
            try {
                viewComp(comp);
            } catch (err) {
                console.error('Error viewing comp:', err);
            }
        }
        // Trigger highlight callback if provided
        if (onCompClick && comp) {
            try {
                onCompClick(comp);
            } catch (err) {
                console.error('Error in onCompClick:', err);
            }
        }
    };

    if (compsWithAdjustments.length === 0) {
        return null;
    }

    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return '';
        return formatterCompact.format(price);
    };

    return (
        <div className="comps-distribution-container">
            <div className="comps-distribution-header">
                <span className="body-bold colour-primary">Price distribution</span>
                <OptionSwitch
                    labels={["Adj. values", "Sale price"]}
                    firstOption={showAdjustedValues}
                    optionSwitch={() => setShowAdjustedValues(!showAdjustedValues)}
                    leftWidth={0.5}
                    rightWidth={0.5}
                    switchWidth={200}
                    disabled={false}
                />
            </div>
            
            {selectedBucket && (() => {
                const displayBucket = hoveredBucket || selectedBucket;
                const displaySegment = hoveredBucket 
                    ? bucketSegments.find(s => s.bucket === hoveredBucket)
                    : bucketSegments.find(s => s.isSelected);
                const arrowLeft = displaySegment 
                    ? displaySegment.left + (displaySegment.width / 2)
                    : 50;
                const isHoveringUnselected = hoveredBucket && hoveredBucket.confidence !== riskSpectrum?.recommendation?.confidence;
                
                return (
                    <div className={`risk-spectrum-container ${isHoveringUnselected ? 'risk-spectrum-container-error' : ''}`}>
                        <div 
                            className={`risk-spectrum-arrow ${isHoveringUnselected ? 'risk-spectrum-arrow-error' : ''}`}
                            style={{
                                '--arrow-position': `${arrowLeft}%`
                            }}
                        />
                    <div className="risk-spectrum-message-box" ref={messageBoxRef}>
                        <span className="risk-spectrum-label body-bold">
                            {formatPriceRange(displayBucket.priceRange)}
                        </span>
                        {displayBucket.explanation && (
                            <span 
                                className="risk-spectrum-text body-regular"
                                dangerouslySetInnerHTML={{ 
                                    __html: renderHTML(displayBucket.explanation, allComps, { clickable: true }) 
                                }}
                            />
                        )}
                    </div>
                </div>
                );
            })()}

            <div className={`comps-distribution-range-container ${!selectedBucket ? 'comps-distribution-range-container-no-risk' : ''}`}>
                <div className="comps-distribution-spectrum-wrapper">
                    <div className="comps-distribution-spectrum">
                        {bucketSegments.map((segment, index) => (
                            <div
                                key={`bucket-segment-${index}`}
                                className={`risk-spectrum-segment ${segment.isSelected ? 'risk-spectrum-segment-selected' : 'risk-spectrum-segment-unselected'}`}
                                style={{
                                    '--segment-left': `${segment.left}%`,
                                    '--segment-width': `${segment.width}%`
                                }}
                                onMouseEnter={() => {
                                    if (segment.isSelected) {
                                        setHoveredBucket(null);
                                    } else {
                                        setHoveredBucket(segment.bucket);
                                    }
                                }}
                            />
                        ))}
                        {dividerPositions.map((position, index) => (
                            <div
                                key={`divider-${index}`}
                                className="comps-distribution-divider"
                                style={{
                                    left: `${position}%`
                                }}
                            />
                        ))}
                        {compPositions.map(({ comp, position, pixelOffset }, index) => {
                        const compZpid = comp.zpid || comp.uniqueIds?.zillowId;
                        const isSelected = props.clickedZPID && compZpid && String(props.clickedZPID) === String(compZpid);
                        
                        return (
                            <div
                                key={compZpid || index}
                                className={`comp-dot ${isSelected ? 'comp-dot-selected' : ''}`}
                                style={{ 
                                    left: `${position}%`,
                                    transform: `translate(calc(-50% + ${pixelOffset}px), -50%)`
                                }}
                                onClick={() => handleCompClick(comp)}
                                title={`${comp.addressDetails?.streetAddress || 'Unknown'}: ${formatterCompact.format(showAdjustedValues ? comp.adjustedValue : getPriceField(comp))}`}
                            >
                                <div className="comp-dot-inner" />
                            </div>
                        );
                        })}
                    </div>
                </div>
                <div className="comps-distribution-range-labels">
                    <span className="label-regular colour-secondary">
                        {formatterCompact.format(fixedMinValue)}
                    </span>
                    <span className="label-regular colour-secondary">
                        {formatterCompact.format(fixedMaxValue)}
                    </span>
                </div>
            </div>
        </div>
    );
}

export default CompsDistribution;

